import * as React from 'react';
import {
  Avatar,
  Button,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import { getComments } from '../../controllers/posts/comments';
import CommentReplyDrawer from './CommentReplyDrawer';
import { DeletePost } from './card/PostActions';
import { UserContext } from '../../context/UserContext';

const NAV = '#202c41';
const MUTED = '#8A95A3';

const getTimeAgo = (dateString) => {
  const diffInMinutes = Math.floor((new Date() - new Date(dateString)) / (1000 * 60));
  if (diffInMinutes < 1) return 'now';
  if (diffInMinutes < 60) return `${diffInMinutes}m`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
  if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d`;
  return new Date(dateString).toLocaleDateString();
};

// Styled components for better layout
const CommentContainer = styled(Box)(({ theme, isReply }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  marginLeft: isReply ? theme.spacing(6) : 0,
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

const CommentContent = styled(Box)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(1.5),
}));

const CommentActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(0.5),
}));

const CommentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  marginBottom: theme.spacing(0.25),
}));

const CommentText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  lineHeight: 1.5,
  wordBreak: 'break-word',
}));

const DrawerContainer = styled(Box)(({ theme }) => ({
  maxHeight: '60vh',
  overflowY: 'auto',
  padding: theme.spacing(1),
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(1),
}));

export default function SlimCommentDrawer({count, id, token, tracking}) {
  const [comments, setComments] = React.useState([])
  const [content, setContent] = React.useState("")
  const [showReplies, setShowReplies] = React.useState([])
  const [reply_id, setReplyId] = React.useState(id)
  const messagesEndRef = React.createRef()
  const messagesRef = React.createRef()
  const { userDetails } = React.useContext(UserContext)
  const navigate = useNavigate()

  const getAPIComments = async ( ) => {
    setComments(await getComments(id, token))
    setContent("")
  }

  React.useEffect(() => {
    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
      scrollToBottom()
  }, [messagesEndRef])

  React.useEffect(() => {
    const getAPIComments = async ( ) => {
      setComments(await getComments(id, token))
      setContent("")
    }

    getAPIComments()
    setReplyId(id)
  }, [token, id])


  React.useEffect(() => {
    const reply_ids = []
    comments?.descendants?.filter(item => item.in_reply_to_id === id && reply_ids.push(item.in_reply_to_id))
    setShowReplies(reply_ids)
  }, [comments?.descendants, id])

  const repliesToggle = (id) => {
    if(showReplies.includes(id)){
      let removedReply = showReplies.filter(function(item) {
        return item !== id
      })
      setShowReplies(removedReply)
    } else {
      setShowReplies([...showReplies, id])
    }
  }

  const replyCount = (id) => {
    let count = comments?.descendants?.filter(item => item.in_reply_to_id === id).length
    return count
  }

  if(!comments){
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  const comments_drawer = () => (
    <DrawerContainer>
      {comments?.descendants?.length > 0 ? (
        comments?.descendants?.map((comment, i) =>
          showReplies.includes(comment.in_reply_to_id) && (
            <CommentContainer
              key={comment.id}
              isReply={comment.in_reply_to_id !== id}
              ref={comment.id === comments?.descendants[comments?.descendants?.length-1].id ? messagesEndRef : messagesRef}
            >
              <Avatar
                alt={comment.account.username}
                src={comment.account.avatar}
                onClick={() => navigate('/profile/' + comment.account.id)}
                sx={{ width: 40, height: 40, cursor: 'pointer' }}
              />

              <CommentContent>
                <CommentHeader>
                  <Typography
                    variant="subtitle2"
                    component="span"
                    fontWeight={700}
                    sx={{ color: NAV }}
                  >
                    {comment.account.display_name || comment.account.username}
                  </Typography>
                  <Typography variant="caption" sx={{ color: MUTED }}>
                    @{comment.account.acct} · {getTimeAgo(comment.created_at)}
                  </Typography>
                </CommentHeader>

                <CommentText variant="body2">
                  {comment.content.replaceAll(/(<([^>]+)>)/ig, '').replaceAll('&#39;', "'")}
                </CommentText>

                <CommentActions>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => repliesToggle(comment.id)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    {replyCount(comment.id)} Replies
                  </Button>

                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      setContent("@"+comment.account.acct+" ")
                      setReplyId(comment.id)
                    }}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      color: 'text.secondary'
                    }}
                  >
                    Reply
                  </Button>
                </CommentActions>
              </CommentContent>

              {userDetails?.id === comment.account.id && (
                <Box ml="auto">
                  <DeletePost id={comment.id} token={token} dontRedirect={getAPIComments} />
                </Box>
              )}
            </CommentContainer>
          )
        )
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100px"
          textAlign="center"
        >
          <Typography variant="body2" color="text.secondary">
            Be the first to comment
          </Typography>
        </Box>
      )}

      <Box mt={2}>
        <CommentReplyDrawer
          id={reply_id}
          token={token}
          content={content}
          setContent={setContent}
          getAPIComments={getAPIComments}
        />
      </Box>
    </DrawerContainer>
  )

  return (
    <Box>
      <HeaderContainer>
        <Typography variant="h6" fontWeight="bold">
          Group Chat
        </Typography>
        {count && (
          <Typography variant="caption" color="text.secondary">
            {count} comments
          </Typography>
        )}
      </HeaderContainer>

      {comments_drawer()}
    </Box>
  );
}
